import React, { useState } from 'react';
import PropTypes from 'prop-types';
import Popup from 'reactjs-popup';

import V3PerformerPicker from '../../OfferEditUI/components/V3PerformerPicker';
import client from '../../../shared/api/client';
import { toastError, toastSuccess, errorText } from '../../../shared/toastHelper';
import { ARTIST_PICKER_MODAL_STYLE } from './artistPickerModalStyle';

// Field-shaped entry point to the universal artist picker (O-4907). Renders
// as a form control showing the current selection; clicking it opens
// V3PerformerPicker in a centered modal. Replaces the per-form AsyncSelect
// artist typeaheads so every "pick an artist" field shares one search UI.
//
// value is { value, label } (react-select option shape, what the legacy
// fields already stored). onSelect receives the picker submission —
// { artistId, artist, ... } where `artist` is the full row from fetchOptions
// — and the modal closes once it resolves.
//
// Creation is on by default (the universal picker's whole point): a create
// submission (newArtistName ± spotifyId) is resolved here via POST /artists
// before onSelect fires, so consumers only ever receive a real artistId — no
// per-call-site create handling. Pass allowCreate={false} only where a
// just-created artist would dead-end (see OutreachModal).
//
// Controlled mode: pass open + onOpenChange to drive the modal from your own
// trigger(s); the built-in field-shaped trigger is not rendered.
const ArtistPickerField = ({
  id, value, placeholder, description, fetchOptions, allowCreate, teamId, onSelect,
  open: controlledOpen, onOpenChange,
}) => {
  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = typeof onOpenChange === 'function';
  const open = isControlled ? !!controlledOpen : internalOpen;
  const setOpen = isControlled ? onOpenChange : setInternalOpen;

  const handleSubmit = async (sel) => {
    let submission = sel;

    if (!sel.artistId) {
      let data;
      try {
        ({ data } = await client.post('/artists', {
          artist: {
            name: sel.newArtistName,
            spotify_id: sel.spotifyId || null,
            external_url: sel.externalUrl || null,
          },
        }));
      } catch (e) {
        toastError(errorText(e));
        return;
      }
      // resolve_or_build_inline reuses exact/merged-name matches, so the
      // resolved artist can differ from what the user typed — say so.
      if (data.name && data.name !== sel.newArtistName) {
        toastSuccess(`Matched existing artist "${data.name}"`);
      }
      submission = { ...sel, artistId: data.id, newArtistName: null, artist: data };
    }

    // Consumers are expected to handle their own attach errors, but a
    // rejection here must not strand the user in a modal that looks frozen —
    // and by this point any created artist already exists, so a retry is safe.
    try {
      await onSelect(submission);
    } catch (e) {
      toastError(errorText(e));
      return;
    }
    setOpen(false);
  };

  return (
    <>
      {!isControlled && (
        <button
          type="button"
          id={id}
          className="form-control text-left"
          onClick={() => setOpen(true)}
          aria-haspopup="dialog"
          aria-expanded={open}
          data-testid="artist-picker-field"
        >
          {value && value.label ? value.label : <span className="text-muted">{placeholder}</span>}
        </button>
      )}
      <Popup
        open={open}
        onClose={() => setOpen(false)}
        modal
        closeOnDocumentClick
        contentStyle={ARTIST_PICKER_MODAL_STYLE}
        // Several consumers (Follow-up, Outreach, Attach-artist) open this
        // picker from inside another popup, so the overlay has to out-stack
        // theirs. Kept just under react-toastify's 9999 so a failure toast
        // still paints above the dimmed backdrop.
        overlayStyle={{ zIndex: 9998 }}
      >
        <V3PerformerPicker
          mode="edit"
          variant="modal"
          currentPerformer={value && value.label ? { name: value.label } : null}
          description={description}
          fetchOptions={fetchOptions}
          allowCreate={allowCreate}
          teamId={teamId}
          onClose={() => setOpen(false)}
          onSubmit={handleSubmit}
        />
      </Popup>
    </>
  );
};

ArtistPickerField.propTypes = {
  id: PropTypes.string,
  value: PropTypes.shape({
    value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    label: PropTypes.string,
  }),
  placeholder: PropTypes.string,
  description: PropTypes.string,
  fetchOptions: PropTypes.func,
  allowCreate: PropTypes.bool,
  teamId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onSelect: PropTypes.func.isRequired,
  open: PropTypes.bool,
  onOpenChange: PropTypes.func,
};

ArtistPickerField.defaultProps = {
  id: undefined,
  value: null,
  placeholder: 'Search artists…',
  description: 'Search any artist on Opendate.',
  fetchOptions: undefined,
  allowCreate: true,
  teamId: null,
  open: undefined,
  onOpenChange: undefined,
};

export default ArtistPickerField;
